import { z } from 'zod'

/** How a new company is billed: never, or through Checkout with a trial. */
export const TENANT_PLANS = ['comped', 'paid'] as const
export const tenantPlanSchema = z.enum(TENANT_PLANS)
export type TenantPlan = z.infer<typeof tenantPlanSchema>

/** Create a tenant (platform SUPER_ADMIN only). Its first admin user is added
 * separately. */
export const createTenantInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(160),
    /**
     * `comped` — never billed. `paid` — the first organization admin invited
     * finishes signing up (terms, first venue, Checkout with the trial) and
     * becomes the billing owner.
     */
    plan: tenantPlanSchema.default('comped'),
  })
  .strict()

export type CreateTenantInput = z.infer<typeof createTenantInputSchema>

/** Rename a tenant. */
export const updateTenantInputSchema = createTenantInputSchema.pick({ name: true }).partial().strict()

export type UpdateTenantInput = z.infer<typeof updateTenantInputSchema>

/** Switch one feature on or off for a tenant (platform SUPER_ADMIN only). */
export const setTenantFeatureInputSchema = z
  .object({
    enabled: z.boolean(),
  })
  .strict()

export type SetTenantFeatureInput = z.infer<typeof setTenantFeatureInputSchema>

/** Comp a tenant, or end its comp (platform SUPER_ADMIN only). */
export const setTenantCompInputSchema = z
  .object({
    comped: z.boolean(),
  })
  .strict()

export type SetTenantCompInput = z.infer<typeof setTenantCompInputSchema>

/**
 * Add days to a tenant's trial or grace period (platform SUPER_ADMIN only). The
 * API refuses an extension that would take one trial or one grace period past
 * 14 days in total.
 */
export const extendTenantBillingInputSchema = z
  .object({
    days: z.number().int().min(1).max(14),
  })
  .strict()

export type ExtendTenantBillingInput = z.infer<typeof extendTenantBillingInputSchema>
